import { Fragment, useMemo, useState, type KeyboardEvent } from 'react';
import {
  addDays,
  formatHours,
  mondayOf,
  parseHours,
  toWireDate,
  type DealLineItem,
  type DealPlaceholder,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  Empty,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  cn,
  type MenuItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import type { DealTabProps } from './deal-tab';
import { dealMoney, initialsOf, seniorityLabel } from './deal-kit';
import { PlanConfirm } from './plan-kit';
import { lineHasWork } from './plan-line-dialog';
import { PlaceholderDialog } from './resource-placeholder-dialog';
import { isoWeek, mondaysBetween, shortDate } from './resource-weeks';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · the record's *Erőforrás* — DM-4, DM-5.       │
 * │ `phase6/09-deal-resource.png`, `09b-deal-resource-add.png`.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The plan's figures, then a weekly grid: an allocation cell is (line item × placeholder × week).
 *
 * ⚠️⚠️ **A PLACEHOLDER BELONGS TO THE DEAL, NOT TO A LINE ITEM** — one *Senior backend* can carry
 * hours on three line items, and the mockup draws it under each of them. So the grid shows, per
 * line item, the placeholders that HAVE hours there, plus any put there from that line item's
 * *+ Allokáció* menu this visit. ⚠️ The second set lives on this screen only: the server stores
 * hours, not an empty pairing, so a row nobody typed into is gone after a reload — the placeholder
 * itself stays, listed under *Órák nélküli placeholderek* until it is put somewhere again.
 *
 * ⚠️ **Rows are the line items that carry work** — fixed and time-and-material (`lineHasWork`). A
 * licence and an option are priced, not worked; one is still drawn if it somehow holds hours
 * (its type changed after planning), so no hour on the deal is ever invisible here.
 *
 * ⚠️ **Edits are drafts until saved** (`state-rules.md` §8 — blur does not save). Every changed
 * cell batches into one `PUT /allocations`; Enter in a cell saves the batch, Escape drops that
 * cell's draft. The totals move with the drafts, so the figure being typed is the one summed.
 *
 * ⚠️ **The weeks span the plan** (`plan.startsOn..endsOn`, or twelve weeks from today when no line
 * item has dates), widened to cover every week that holds hours, and the arrows add four weeks
 * at either end rather than paging — a plan is read across, not one month at a time.
 */

const EXTEND = 4;
const MAX_HOURS = 400;

type CellKey = `${string}|${string}|${string}`;
const cellKey = (lineItemId: string, placeholderId: string, week: string): CellKey =>
  `${lineItemId}|${placeholderId}|${week}`;
const pairKey = (lineItemId: string, placeholderId: string): string =>
  `${lineItemId}|${placeholderId}`;

export function DealResourceTab({ deal, record, feeds, canManage, onTab }: DealTabProps) {
  const t = useT();
  const { locale } = useI18n();
  const rateCurrency = feeds.config?.settings.currencyCode ?? deal.currencyCode;
  const tentativeOn = feeds.config?.settings.tentativeBooking ?? false;

  const [earlier, setEarlier] = useState(0);
  const [later, setLater] = useState(0);
  const [drafts, setDrafts] = useState<ReadonlyMap<CellKey, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Pairings put there from a menu this visit — see the header. */
  const [attached, setAttached] = useState<ReadonlySet<string>>(new Set());
  /**
   * A placeholder being made from a line item's menu: whichever placeholder the answer holds that
   * `before` did not is that line item's. Folded into `attached` when the next one starts, so it
   * is derived rather than written back from an effect.
   */
  const [claim, setClaim] = useState<{
    lineItemId: string;
    before: ReadonlySet<string>;
  } | null>(null);
  const [editing, setEditing] = useState<DealPlaceholder | null>(null);
  const [pending, setPending] = useState<
    | { kind: 'delete'; placeholder: DealPlaceholder }
    | { kind: 'detach'; placeholder: DealPlaceholder; line: DealLineItem; hours: number }
    | null
  >(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  /* ── what the server holds ── */
  const saved = useMemo(() => {
    const map = new Map<CellKey, number>();
    for (const allocation of deal.allocations) {
      map.set(
        cellKey(allocation.lineItemId, allocation.placeholderId, allocation.weekStart),
        Number(allocation.hours),
      );
    }
    return map;
  }, [deal.allocations]);

  const pairs = useMemo(() => {
    const set = new Set(attached);
    for (const allocation of deal.allocations) {
      set.add(pairKey(allocation.lineItemId, allocation.placeholderId));
    }
    if (claim !== null) {
      for (const placeholder of deal.placeholders) {
        if (!claim.before.has(placeholder.id)) set.add(pairKey(claim.lineItemId, placeholder.id));
      }
    }
    return set;
  }, [attached, claim, deal.allocations, deal.placeholders]);

  /* ── the weeks ── */
  const weeks = useMemo(() => {
    const today = toWireDate(new Date());
    let from = mondayOf(deal.plan.startsOn ?? today);
    let to = deal.plan.endsOn === null ? addDays(from, 7 * 11) : mondayOf(deal.plan.endsOn);
    for (const allocation of deal.allocations) {
      if (allocation.weekStart < from) from = allocation.weekStart;
      if (allocation.weekStart > to) to = allocation.weekStart;
    }
    return mondaysBetween(addDays(from, -7 * earlier), addDays(to, 7 * later));
  }, [deal.plan.startsOn, deal.plan.endsOn, deal.allocations, earlier, later]);
  const thisWeek = mondayOf(toWireDate(new Date()));

  /* ── the rows ── */
  const lines = deal.lineItems.filter(
    (line) =>
      lineHasWork(line.type) ||
      deal.allocations.some((allocation) => allocation.lineItemId === line.id),
  );
  const rowsOf = (line: DealLineItem): DealPlaceholder[] =>
    deal.placeholders.filter((placeholder) => pairs.has(pairKey(line.id, placeholder.id)));
  const unattached = deal.placeholders.filter(
    (placeholder) => !deal.lineItems.some((line) => pairs.has(pairKey(line.id, placeholder.id))),
  );

  /** A cell's figure: its valid draft, else what is saved. */
  const hoursAt = (key: CellKey): number => {
    const draft = drafts.get(key);
    if (draft !== undefined) {
      const parsed = draft.trim() === '' ? 0 : parseHours(draft);
      if (parsed !== null) return parsed;
    }
    return saved.get(key) ?? 0;
  };

  /* ── writes ── */
  const saveDrafts = async (): Promise<void> => {
    if (drafts.size === 0 || busy) return;
    const cells: { lineItemId: string; placeholderId: string; weekStart: string; hours: number }[] =
      [];
    for (const [key, text] of drafts) {
      const hours = text.trim() === '' ? 0 : parseHours(text);
      if (hours === null) {
        setError(t('dealResource.error.hours'));
        return;
      }
      if (hours > MAX_HOURS) {
        setError(t('dealResource.error.max'));
        return;
      }
      const [lineItemId = '', placeholderId = '', weekStart = ''] = key.split('|');
      /* An untouched value typed back to what it was is not a change worth a write. */
      if ((saved.get(key) ?? 0) === hours) continue;
      cells.push({ lineItemId, placeholderId, weekStart, hours });
    }
    if (cells.length === 0) {
      setDrafts(new Map());
      return;
    }
    setBusy(true);
    setError(null);
    let refusal: string | null = null;
    /* The contract takes 500 cells a request — a grid edit is far below that, a paste may not be. */
    for (let at = 0; at < cells.length && refusal === null; at += 500) {
      refusal = await record.write('/allocations', 'PUT', { cells: cells.slice(at, at + 500) });
    }
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    setDrafts(new Map());
  };

  const attach = (lineItemId: string, placeholderId: string): void => {
    setAttached((current) => new Set(current).add(pairKey(lineItemId, placeholderId)));
  };

  /** Fold the claim in flight into `attached`, then start a new one for this line item. */
  const startClaim = (lineItemId: string): void => {
    if (claim !== null) {
      const folded = new Set(attached);
      for (const placeholder of deal.placeholders) {
        if (!claim.before.has(placeholder.id)) {
          folded.add(pairKey(claim.lineItemId, placeholder.id));
        }
      }
      setAttached(folded);
    }
    setClaim({ lineItemId, before: new Set(deal.placeholders.map((one) => one.id)) });
  };

  const create = async (lineItemId: string, body: Record<string, string>): Promise<void> => {
    setError(null);
    startClaim(lineItemId);
    const refusal = await record.write('/placeholders', 'POST', body);
    if (refusal !== null) setError(refusal);
  };

  const detachLocally = (lineItemId: string, placeholderId: string): void => {
    const key = pairKey(lineItemId, placeholderId);
    setAttached((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    /* A claim that produced this pairing gives it up too. */
    if (claim?.lineItemId === lineItemId && !claim.before.has(placeholderId)) {
      setClaim({ ...claim, before: new Set(claim.before).add(placeholderId) });
    }
    setDrafts((current) => {
      const next = new Map(current);
      for (const draftKey of current.keys()) {
        if (draftKey.startsWith(`${key}|`)) next.delete(draftKey);
      }
      return next;
    });
  };

  const confirmPending = async (): Promise<void> => {
    if (pending === null) return;
    let refusal: string | null = null;
    if (pending.kind === 'delete') {
      refusal = await record.write(`/placeholders/${pending.placeholder.id}`, 'DELETE');
    } else {
      const cells = deal.allocations
        .filter(
          (allocation) =>
            allocation.lineItemId === pending.line.id &&
            allocation.placeholderId === pending.placeholder.id,
        )
        .map((allocation) => ({
          lineItemId: allocation.lineItemId,
          placeholderId: allocation.placeholderId,
          weekStart: allocation.weekStart,
          hours: 0,
        }));
      if (cells.length > 0) refusal = await record.write('/allocations', 'PUT', { cells });
    }
    if (refusal !== null) {
      setPendingError(refusal);
      return;
    }
    if (pending.kind === 'detach') detachLocally(pending.line.id, pending.placeholder.id);
    setPending(null);
  };

  /* ── menus ── */
  const addMenu = (line: DealLineItem): MenuItem[] => {
    const shown = new Set(rowsOf(line).map((one) => one.id));
    const existing = deal.placeholders.filter((one) => !shown.has(one.id));
    const profiles = feeds.config?.profiles ?? [];
    return [
      ...(existing.length === 0
        ? []
        : [
            { type: 'label' as const, label: t('dealResource.menu.existing') },
            ...existing.map((placeholder) => ({
              label: placeholder.name,
              hint: t('dealResource.rate', {
                rate: dealMoney(placeholder.costRate, rateCurrency),
              }),
              dot: placeholder.projectRoleColor ?? 'var(--muted-foreground)',
              onSelect: () => {
                attach(line.id, placeholder.id);
              },
            })),
            { type: 'separator' as const },
          ]),
      { type: 'label' as const, label: t('dealResource.menu.profiles') },
      ...(profiles.length === 0
        ? [{ label: t('dealResource.menu.noProfiles'), disabled: true }]
        : profiles.map((profile) => ({
            label: `${profile.projectRoleName} · ${seniorityLabel(t, profile.seniority)}`,
            hint: t('dealResource.rate', { rate: dealMoney(profile.costRate, rateCurrency) }),
            dot: profile.projectRoleColor,
            onSelect: () => {
              void create(line.id, { profileId: profile.id });
            },
          }))),
      ...(tentativeOn && feeds.members.length > 0
        ? [
            { type: 'separator' as const },
            { type: 'label' as const, label: t('dealResource.menu.people') },
            ...feeds.members.map((member) => ({
              label: member.displayName,
              icon: 'user-round' as const,
              onSelect: () => {
                void create(line.id, { memberId: member.id });
              },
            })),
          ]
        : []),
    ];
  };

  const rowMenu = (placeholder: DealPlaceholder, line: DealLineItem | null): MenuItem[] => [
    {
      label: t('dealResource.row.edit'),
      icon: 'pencil',
      onSelect: () => {
        setEditing(placeholder);
      },
    },
    ...(line === null
      ? []
      : [
          {
            label: t('dealResource.row.detach'),
            icon: 'x' as const,
            onSelect: () => {
              const hours = deal.allocations
                .filter(
                  (allocation) =>
                    allocation.lineItemId === line.id &&
                    allocation.placeholderId === placeholder.id,
                )
                .reduce((sum, allocation) => sum + Number(allocation.hours), 0);
              if (hours === 0) {
                detachLocally(line.id, placeholder.id);
                return;
              }
              setPendingError(null);
              setPending({ kind: 'detach', placeholder, line, hours });
            },
          },
        ]),
    { type: 'separator' },
    {
      label: t('dealResource.row.delete'),
      icon: 'trash',
      destructive: true,
      onSelect: () => {
        setPendingError(null);
        setPending({ kind: 'delete', placeholder });
      },
    },
  ];

  /* ── figures ── */
  const plan = deal.plan;
  const planCost = Number(plan.laborCost) + Number(plan.externalCost);
  const tiles = [
    {
      key: 'allocated',
      label: t('dealResource.tile.allocated'),
      value: formatHours(plan.allocatedHours),
      sub: t('dealResource.tile.estimate', { hours: formatHours(plan.estimateHours) }),
    },
    {
      key: 'cost',
      label: t('dealResource.tile.cost'),
      value: dealMoney(planCost, deal.currencyCode),
      sub: t('dealResource.tile.costSplit', {
        labor: dealMoney(plan.laborCost, deal.currencyCode),
        external: dealMoney(plan.externalCost, deal.currencyCode),
      }),
    },
    {
      key: 'revenue',
      label: t('dealResource.tile.revenue'),
      value: dealMoney(plan.revenue, deal.currencyCode),
      sub: t('dealResource.tile.revenueSub'),
    },
    {
      key: 'margin',
      label: t('dealResource.tile.margin'),
      /* §5 · a margin with no revenue is undefined, not zero. */
      value: plan.marginPct === null ? '—' : `${String(Math.round(plan.marginPct * 100))}%`,
      sub: dealMoney(plan.margin, deal.currencyCode),
    },
  ];

  const weekLabel = (week: string): string => t('dealResource.week', { week: isoWeek(week) });
  const total = (keys: (week: string) => CellKey[]): number[] =>
    weeks.map((week) => keys(week).reduce((sum, key) => sum + hoursAt(key), 0));
  const figure = (hours: number): string => (hours === 0 ? '' : formatHours(hours));

  return (
    <div data-screen="deal-resource" className="flex flex-col pb-10">
      <div className="grid grid-cols-2 border-b md:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="border-r border-b px-4 py-2.5 last:border-r-0 md:border-b-0"
          >
            <div className="text-muted-foreground truncate text-[11.5px]">{tile.label}</div>
            <div className="mt-0.5 text-[19px] font-medium tabular-nums">{tile.value}</div>
            <div className="text-muted-foreground truncate text-[11.5px] tabular-nums">
              {tile.sub}
            </div>
          </div>
        ))}
      </div>

      {lines.length === 0 ? (
        <Empty
          icon="calendar-days"
          title={t('dealResource.empty.title')}
          body={t('dealResource.empty.body')}
          actionLabel={t('dealResource.empty.action')}
          onAction={() => {
            onTab('plan');
          }}
        />
      ) : (
        <>
          {/* ── the window, and the drafts waiting to be saved ── */}
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('dealResource.weeks.earlier')}
              title={t('dealResource.weeks.earlier')}
              onClick={() => {
                setEarlier((count) => count + EXTEND);
              }}
            >
              <Icon name="chevron-left" size={14} />
            </Button>
            <span className="text-[12.5px] tabular-nums" aria-label={t('dealResource.weeks.range')}>
              {`${shortDate(weeks[0] ?? thisWeek, locale)} – ${shortDate(
                addDays(weeks[weeks.length - 1] ?? thisWeek, 6),
                locale,
              )}`}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('dealResource.weeks.later')}
              title={t('dealResource.weeks.later')}
              onClick={() => {
                setLater((count) => count + EXTEND);
              }}
            >
              <Icon name="chevron-right" size={14} />
            </Button>
            <div className="flex-1" />
            {error === null ? null : (
              <span className="text-destructive text-[12px]" role="alert">
                {error}
              </span>
            )}
            {drafts.size === 0 ? null : (
              <>
                <span className="text-muted-foreground text-[12px] tabular-nums">
                  {t('dealResource.drafts', { count: drafts.size })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDrafts(new Map());
                    setError(null);
                  }}
                >
                  {t('dealResource.discard')}
                </Button>
                <Button size="sm" disabled={busy} onClick={() => void saveDrafts()}>
                  {t('common.save')}
                </Button>
              </>
            )}
          </div>

          <Table label={t('dealResource.grid.label')} className="w-max min-w-full">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="bg-background sticky left-0 z-10 min-w-[280px] pl-4">
                  {t('dealResource.column.item')}
                </TableHead>
                <TableHead className="min-w-[110px] text-right">
                  {t('dealResource.column.total')}
                </TableHead>
                {weeks.map((week) => (
                  <TableHead
                    key={week}
                    className={cn(
                      'h-auto min-w-[76px] py-1.5 text-right normal-case',
                      week === thisWeek && 'text-foreground',
                    )}
                    title={t('dealResource.weekOf', { date: shortDate(week, locale) })}
                  >
                    <div className="text-[11.5px] tracking-normal">{shortDate(week, locale)}</div>
                    <div className="text-[10.5px] uppercase tabular-nums">{weekLabel(week)}</div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => {
                const rows = rowsOf(line);
                const lineTotals = total((week) =>
                  rows.map((placeholder) => cellKey(line.id, placeholder.id, week)),
                );
                const lineSum = lineTotals.reduce((sum, hours) => sum + hours, 0);
                return (
                  <Fragment key={line.id}>
                    <TableRow className="bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))] hover:bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))]">
                      <TableCell className="sticky left-0 z-[1] bg-inherit pl-4 font-medium">
                        {line.name}
                      </TableCell>
                      <TableCell
                        numeric
                        className={cn(
                          lineSum > Number(line.estimateHours) &&
                            Number(line.estimateHours) > 0 &&
                            'text-destructive',
                        )}
                      >
                        {`${formatHours(lineSum)} / ${formatHours(line.estimateHours)}`}
                      </TableCell>
                      {lineTotals.map((hours, index) => (
                        <TableCell key={weeks[index]} numeric className="font-medium">
                          {figure(hours)}
                        </TableCell>
                      ))}
                    </TableRow>

                    {rows.length === 0 ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          className="text-muted-foreground bg-background sticky left-0 pl-4 text-[12px]"
                          colSpan={2}
                        >
                          {t('dealResource.noRows')}
                        </TableCell>
                        <TableCell colSpan={weeks.length} />
                      </TableRow>
                    ) : null}

                    {rows.map((placeholder) => {
                      const rowHours = weeks.reduce(
                        (sum, week) => sum + hoursAt(cellKey(line.id, placeholder.id, week)),
                        0,
                      );
                      return (
                        <TableRow key={placeholder.id}>
                          <TableCell className="bg-background sticky left-0 z-[1] py-1 pl-4">
                            <PlaceholderLabel
                              placeholder={placeholder}
                              rateCurrency={rateCurrency}
                              menu={canManage ? rowMenu(placeholder, line) : null}
                            />
                          </TableCell>
                          <TableCell numeric className="text-muted-foreground">
                            {formatHours(rowHours)}
                          </TableCell>
                          {weeks.map((week) => {
                            const key = cellKey(line.id, placeholder.id, week);
                            const draft = drafts.get(key);
                            const stored = saved.get(key) ?? 0;
                            const label = t('dealResource.cell', {
                              name: placeholder.name,
                              week: weekLabel(week),
                            });
                            if (!canManage) {
                              return (
                                <TableCell key={week} numeric aria-label={label}>
                                  {figure(stored)}
                                </TableCell>
                              );
                            }
                            const invalid =
                              draft !== undefined &&
                              draft.trim() !== '' &&
                              (parseHours(draft) ?? MAX_HOURS + 1) > MAX_HOURS;
                            return (
                              <TableCell key={week} className="px-1 py-1">
                                <input
                                  aria-label={label}
                                  aria-invalid={invalid || undefined}
                                  inputMode="decimal"
                                  value={draft ?? figure(stored)}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setError(null);
                                    setDrafts((current) => new Map(current).set(key, value));
                                  }}
                                  onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      void saveDrafts();
                                    } else if (event.key === 'Escape') {
                                      setDrafts((current) => {
                                        const next = new Map(current);
                                        next.delete(key);
                                        return next;
                                      });
                                    }
                                  }}
                                  className={cn(
                                    'hover:ring-input focus:ring-ring/50 h-7 w-full rounded-sm bg-transparent px-1.5 text-right text-[12.5px] tabular-nums outline-none hover:ring-1 focus:ring-2',
                                    draft !== undefined && 'bg-accent/60',
                                    invalid && 'ring-destructive ring-1',
                                  )}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}

                    {canManage ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell className="bg-background sticky left-0 z-[1] py-1 pl-3">
                          <DropdownMenu
                            label={t('dealResource.addAllocationTo', { name: line.name })}
                            items={addMenu(line)}
                            width={300}
                            searchPlaceholder={t('dealResource.menu.search')}
                            emptyLabel={t('dealResource.menu.empty')}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={t('dealResource.addAllocationTo', { name: line.name })}
                            >
                              <Icon name="plus" size={14} />
                              {t('dealResource.addAllocation')}
                            </Button>
                          </DropdownMenu>
                        </TableCell>
                        <TableCell colSpan={weeks.length + 1} />
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}

              {/* ── every line item's hours, per week ── */}
              {(() => {
                const grand = total((week) =>
                  lines.flatMap((line) =>
                    rowsOf(line).map((placeholder) => cellKey(line.id, placeholder.id, week)),
                  ),
                );
                return (
                  <TableRow className="border-t-2 font-medium hover:bg-transparent">
                    <TableCell className="bg-background sticky left-0 z-[1] pl-4">
                      {t('dealResource.total')}
                    </TableCell>
                    <TableCell numeric>
                      {formatHours(grand.reduce((sum, hours) => sum + hours, 0))}
                    </TableCell>
                    {grand.map((hours, index) => (
                      <TableCell key={weeks[index]} numeric>
                        {figure(hours)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </>
      )}

      {unattached.length === 0 ? null : (
        <section className="px-4 pt-5">
          <h2 className="text-[13px] font-medium">{t('dealResource.unattached')}</h2>
          <p className="text-muted-foreground mt-0.5 text-[12px]">
            {t('dealResource.unattached.hint')}
          </p>
          <ul className="mt-2 flex flex-col divide-y rounded-md border">
            {unattached.map((placeholder) => (
              <li key={placeholder.id} className="px-3 py-1.5">
                <PlaceholderLabel
                  placeholder={placeholder}
                  rateCurrency={rateCurrency}
                  menu={canManage ? rowMenu(placeholder, null) : null}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <PlaceholderDialog
        placeholder={editing}
        record={record}
        feeds={feeds}
        onClose={() => {
          setEditing(null);
        }}
      />
      <PlanConfirm
        open={pending !== null}
        title={
          pending?.kind === 'detach'
            ? t('dealResource.confirm.detachTitle')
            : t('dealResource.confirm.deleteTitle')
        }
        body={
          pending?.kind === 'detach'
            ? t('dealResource.confirm.detachBody', {
                name: pending.placeholder.name,
                hours: formatHours(pending.hours),
              })
            : t('dealResource.confirm.deleteBody', { name: pending?.placeholder.name ?? '' })
        }
        confirmLabel={
          pending?.kind === 'detach' ? t('dealResource.row.detach') : t('common.delete')
        }
        error={pendingError}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => void confirmPending()}
      />
    </div>
  );
}

/**
 * A placeholder's row head: its role colour (or the person's initials, for a tentative booking),
 * its name, its cost rate and headcount, and the row menu.
 *
 * ⚠️ **DM-5 · a tentative booking is marked AND explained** — a real person's name on a plan reads
 * as a commitment, and the badge's tooltip says what it is not: hidden from the resource planner
 * behind its toggle, never counted in utilisation.
 */
function PlaceholderLabel({
  placeholder,
  rateCurrency,
  menu,
}: {
  readonly placeholder: DealPlaceholder;
  readonly rateCurrency: string;
  readonly menu: MenuItem[] | null;
}) {
  const t = useT();
  const tentative = placeholder.memberId !== null;
  const color = placeholder.projectRoleColor ?? 'var(--muted-foreground)';
  const details = [
    t('dealResource.rate', { rate: dealMoney(placeholder.costRate, rateCurrency) }),
    placeholder.seniority === null ? null : seniorityLabel(t, placeholder.seniority),
    placeholder.headcount > 1
      ? t('dealResource.headcount', { count: placeholder.headcount })
      : null,
    placeholder.memberName !== null && placeholder.memberName !== placeholder.name
      ? placeholder.memberName
      : null,
    tentative && placeholder.projectRoleName !== null ? placeholder.projectRoleName : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="flex items-center gap-2.5">
      {tentative ? (
        <span
          aria-hidden
          className="grid size-6 shrink-0 place-items-center rounded-full border border-dashed text-[10px] font-medium"
          style={{ borderColor: color }}
        >
          {initialsOf(placeholder.memberName ?? placeholder.name)}
        </span>
      ) : (
        <span
          aria-hidden
          className="grid size-6 shrink-0 place-items-center rounded-full border border-dashed"
          style={{ borderColor: color }}
        >
          <span className="size-2 rounded-full" style={{ background: color }} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px]">{placeholder.name}</span>
          {tentative ? (
            <Tooltip content={t('dealResource.tentative.hint')} width={280}>
              <Badge variant="outline" tabIndex={0} className="border-dashed">
                {t('dealResource.tentative')}
              </Badge>
            </Tooltip>
          ) : null}
        </div>
        <div className="text-muted-foreground truncate text-[11.5px] tabular-nums">
          {details.join(' · ')}
        </div>
      </div>
      {menu === null ? null : (
        <DropdownMenu label={t('dealResource.row.menu')} align="end" items={menu}>
          <Button variant="ghost" size="icon-sm" aria-label={t('dealResource.row.menu')}>
            <Icon name="ellipsis" size={16} />
          </Button>
        </DropdownMenu>
      )}
    </div>
  );
}
